/**
 * LandingDailyQuote — picks one quote per day and shows it.
 * Dashboard-style card: icon chip header, bold quote, hairline attribution.
 */
import { useEffect, useState } from 'react';
import { Quote } from 'lucide-react';

const QUOTES = [
    { title: 'Quote Of The Day', quote: 'The greatest wealth is health.', author: '- Virgil' },
    { title: 'Longevity Mindset', quote: 'Take care of your body. It\'s the only place you have to live.', author: '- Jim Rohn' },
    { title: 'Innovation Boost', quote: 'Innovation is seeing what everybody has seen and thinking what nobody has thought.', author: '- Albert Szent-Györgyi' },
    { title: 'Growth Mindset', quote: 'Your life does not get better by chance, it gets better by change.', author: '- Jim Rohn' },
    { title: 'Discipline Boost', quote: 'Discipline is choosing what you want most over what you want now.', author: '- Abraham Lincoln' },
    { title: 'Focus Mode', quote: 'Starve your distractions, feed your focus.', author: '- Anonymous' },
    { title: 'Hustle Spirit', quote: 'Great things never come from comfort zones.', author: '- Anonymous' },
    { title: 'Energy Booster', quote: 'Your energy introduces you before you speak.', author: '- Anonymous' },
    { title: 'Peak Potential', quote: 'Success usually comes to those who are too busy to be looking for it.', author: '- Henry David Thoreau' },
    { title: 'Momentum Charge', quote: 'Do something today your future self will thank you for.', author: '- Anonymous' },
    { title: 'Winning Habit', quote: 'Motivation gets you started. Habit keeps you going.', author: '- Jim Rohn' },
    { title: 'Clarity Boost', quote: 'Where focus goes, energy flows.', author: '- Tony Robbins' },
    { title: 'Goal Setter', quote: 'A goal without a plan is just a wish.', author: '- Antoine de Saint-Exupéry' },
    { title: 'Drive Mode', quote: 'You miss 100% of the shots you don\'t take.', author: '- Wayne Gretzky' },
    { title: 'Persistence Mode', quote: 'Fall seven times, stand up eight.', author: '- Japanese Proverb' },
    { title: 'Vitality Quote', quote: 'Age is an issue of mind over matter. If you don\'t mind, it doesn\'t matter.', author: '- Mark Twain' },
    { title: 'Wellness First', quote: 'He who has health has hope, and he who has hope has everything.', author: '- Arabic Proverb' },
    { title: 'Science of Life', quote: 'The body achieves what the mind believes.', author: '- Anonymous' },
    { title: 'Impact Quote', quote: 'Make your life a masterpiece; imagine no limitations.', author: '- Brian Tracy' },
    { title: 'Performance Push', quote: 'Don\'t watch the clock; do what it does. Keep going.', author: '- Sam Levenson' },
];

export default function LandingDailyQuote() {
    const [card, setCard] = useState(null);

    useEffect(() => {
        const today = new Date().toISOString().split('T')[0];
        const saved = JSON.parse(localStorage.getItem('ageon_dailyQuote') || 'null');
        if (saved?.date === today && saved?.index < QUOTES.length) {
            setCard(QUOTES[saved.index]);
        } else {
            const idx = Math.floor(Math.random() * QUOTES.length);
            localStorage.setItem('ageon_dailyQuote', JSON.stringify({ date: today, index: idx }));
            setCard(QUOTES[idx]);
        }
    }, []);

    if (!card) return null;

    return (
        <div className="lp-lift relative h-full flex flex-col overflow-hidden rounded-3xl bg-white p-7 lg:p-8 shadow-card ring-1 ring-ink-100">
            <div aria-hidden className="pointer-events-none absolute -top-10 -right-10 h-32 w-32 rounded-full bg-brand-teal/10 blur-2xl" />

            <div className="flex items-center gap-3 mb-6">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-brand-teal to-brand-teal-700 text-white shadow-lg shadow-brand-teal/20">
                    <Quote className="h-[1.1rem] w-[1.1rem]" />
                </span>
                <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-ink-500">
                    {card.title}
                </span>
            </div>

            <blockquote className="font-heading font-bold text-xl sm:text-2xl text-ink-900 leading-[1.45] tracking-tight flex-1">
                &ldquo;{card.quote}&rdquo;
            </blockquote>

            <cite className="not-italic block mt-6 pt-5 border-t border-ink-100 text-sm font-semibold text-brand-teal-700">
                {card.author}
            </cite>
        </div>
    );
}
